import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowUpDown, CheckSquare, Info, Keyboard } from 'lucide-react';

export const RecallListGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Recall List & Search'
      description='Comprehensive searchable list of all recalls with advanced filtering and sorting'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Recall List' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Overview Section */}
        <section className='mb-20'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mb-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  What is the Recall List?
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  The Recall List is your table-based view of recalls - perfect for searching,
                  sorting, and bulk operations. While the Recall Map provides geographic
                  visualization, the Recall List offers a detailed, sortable table view ideal for
                  finding specific recalls, performing bulk selections, and managing large numbers
                  of recalls efficiently.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Accessing the Recall List Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Accessing the Recall List</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            Navigate to the Recall List from the sidebar:
          </p>
          <p className='text-sm leading-6'>
            <strong>Sidebar → SEARCH → Recall List</strong>
          </p>
        </section>

        {/* Understanding the Interface Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Interface</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            The Recall List interface is organized into three main areas:
          </p>
          <ul className='space-y-2 ml-6 mb-4'>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Top:</strong> Filter accordion (same powerful
              filters as the Recall Map)
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Middle:</strong> Data table with sortable columns
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Bottom:</strong> Pagination controls
            </li>
          </ul>
        </section>

        {/* Table Columns Explained Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Table Columns Explained</h2>

          <div className='space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 1: VIN</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Full 17-character VIN</li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Click to copy to clipboard
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Remove button to delete recall
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>Searchable</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 2: Campaign</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Campaign number</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "24V123"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Shows advance notice indicator when available
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Shows paused indicator when campaign is paused
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 3: Description</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Brief description of the recall issue
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Example: "Air bag inflator replacement"
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>Searchable text</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 4: Year</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Vehicle model year</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "2020"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Sortable column</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 5: Make</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Vehicle manufacturer</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "Honda"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Sortable column</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 6: Model</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Vehicle model name</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "Accord"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Sortable column</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 7: Dealer</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Dealership name</li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Example: "Downtown Honda"
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>Sortable column</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 8: Address</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Dealer street address</li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Example: "123 Main Street"
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>Helpful for routing</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 9: City</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Dealer city location</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "Detroit"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Sortable column</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 10: ZIP</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>Dealer ZIP code</li>
                  <li className='text-sm leading-6 text-muted-foreground'>Example: "48201"</li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Useful for geographic filtering
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Column 11: Route</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2'>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    <strong>Add to Route</strong> button when recall is not selected
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    <strong>Remove from Route</strong> button when recall is already in route
                  </li>
                  <li className='text-sm leading-6 text-muted-foreground'>
                    Manage route assignments for technicians
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Sorting the Table Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Sorting the Table</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            Click any column header to sort the data:
          </p>
          <ul className='space-y-2 ml-6 mb-10'>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>First click:</strong> Ascending (A→Z, 0→9,
              oldest→newest)
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Second click:</strong> Descending (Z→A, 9→0,
              newest→oldest)
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Third click:</strong> Remove sort
            </li>
          </ul>

          <h3 className='text-lg font-semibold mb-4'>Common Sorting Uses</h3>

          <div className='grid gap-4 md:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold flex items-center gap-2'>
                  <ArrowUpDown className='h-4 w-4 text-primary' />
                  Sort by Year (descending)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-foreground'>See newest vehicles first</p>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Prioritize customer retention
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold flex items-center gap-2'>
                  <ArrowUpDown className='h-4 w-4 text-primary' />
                  Sort by Dealer Name (ascending)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-foreground'>Group by dealer alphabetically</p>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Plan dealer-specific routes
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold flex items-center gap-2'>
                  <ArrowUpDown className='h-4 w-4 text-primary' />
                  Sort by Campaign
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-foreground'>Group by recall campaign</p>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Campaign-focused work planning
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold flex items-center gap-2'>
                  <ArrowUpDown className='h-4 w-4 text-primary' />
                  Sort by Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-foreground'>Group by completion status</p>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Track progress and priorities
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Using Filters Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Using Filters</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            The Recall List uses the same powerful filter system as the Recall Map. Filters are
            organized into collapsible accordion sections:
          </p>
          <ul className='space-y-2 ml-6 mb-6'>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Vehicle Details:</strong> VIN, Make, Model, Year
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Location:</strong> Dealer, City, ZIP Code, Radius
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Recall Details:</strong> Campaign, Description,
              Route Assignment
            </li>
          </ul>
          <p className='text-sm leading-6 text-muted-foreground'>
            For detailed information about using filters effectively, see the{' '}
            <Link to='/docs/features/filters' className='text-primary hover:underline'>
              Filters System guide
            </Link>
            .
          </p>
        </section>

        {/* Bulk Selection Operations Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Bulk Selection Operations</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-6'>
            The Recall List makes it easy to select multiple recalls for bulk operations:
          </p>

          <Card className='rounded-xl border shadow-sm'>
            <CardHeader className='pb-4'>
              <CardTitle className='text-lg font-bold flex items-center gap-2'>
                <CheckSquare className='h-8 w-8 text-primary' />
                Selection Methods
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                <div>
                  <p className='text-sm leading-6 font-semibold text-foreground'>
                    Single Selection:
                  </p>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Click checkbox on any row
                  </p>
                </div>
                <div>
                  <p className='text-sm leading-6 font-semibold text-foreground'>
                    Range Selection:
                  </p>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Click first checkbox, then Shift+click last checkbox to select all rows in
                    between
                  </p>
                </div>
                <div>
                  <p className='text-sm leading-6 font-semibold text-foreground'>
                    Select All Visible:
                  </p>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Click checkbox in table header to select all rows on current page
                  </p>
                </div>
                <div>
                  <p className='text-sm leading-6 font-semibold text-foreground'>
                    Keyboard Navigation:
                  </p>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Use Space bar to check/uncheck current row (see keyboard shortcuts below)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Pagination Controls Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Pagination Controls</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            At the bottom of the table, you'll find pagination controls:
          </p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Items per page:</strong> Choose to display 25, 50,
              or 100 recalls per page
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Page navigation:</strong> Use Previous/Next
              buttons or jump to specific pages
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              <strong className='text-foreground'>Total count:</strong> See how many total recalls
              match your current filters
            </li>
          </ul>
        </section>

        {/* Keyboard Shortcuts Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Keyboard Shortcuts</h2>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
            <div className='flex gap-4 mb-6'>
              <Keyboard className='h-8 w-8 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  Boost Your Productivity
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Master these keyboard shortcuts to work 3x faster
                </p>
              </div>
            </div>
            <div className='grid gap-6 md:grid-cols-2'>
              <div>
                <h4 className='text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  Navigation
                </h4>
                <div className='space-y-2'>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Tab
                    </Badge>
                    Move to next field/button
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Shift + Tab
                    </Badge>
                    Move to previous field/button
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Enter
                    </Badge>
                    Submit search or activate button
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Esc
                    </Badge>
                    Close expanded row or dialog
                  </div>
                </div>
              </div>
              <div>
                <h4 className='text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  Table Navigation
                </h4>
                <div className='space-y-2'>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      ↓ Arrow
                    </Badge>
                    Move to next row
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      ↑ Arrow
                    </Badge>
                    Move to previous row
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Space
                    </Badge>
                    Check/uncheck current row
                  </div>
                  <div className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                    <Badge variant='outline' className='mr-2'>
                      Shift + Click
                    </Badge>
                    Select range of rows
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search Capabilities Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Search Capabilities</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-6'>
            The Recall List includes quick search functionality for common queries:
          </p>

          <Card className='rounded-xl border shadow-sm'>
            <CardHeader className='pb-4'>
              <CardTitle className='text-lg font-bold flex items-center gap-2'>
                <Search className='h-8 w-8 text-primary' />
                Quick Search Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className='space-y-3 mb-6'>
                <li className='text-sm leading-6'>
                  <strong className='text-foreground'>VIN:</strong>{' '}
                  <span className='text-muted-foreground'>
                    Search by full or partial VIN (last 6 digits work great)
                  </span>
                </li>
                <li className='text-sm leading-6'>
                  <strong className='text-foreground'>Customer Name:</strong>{' '}
                  <span className='text-muted-foreground'>Find recalls by customer name</span>
                </li>
                <li className='text-sm leading-6'>
                  <strong className='text-foreground'>Phone Number:</strong>{' '}
                  <span className='text-muted-foreground'>Search by customer phone number</span>
                </li>
                <li className='text-sm leading-6'>
                  <strong className='text-foreground'>Campaign Number:</strong>{' '}
                  <span className='text-muted-foreground'>Quickly find specific campaigns</span>
                </li>
              </ul>
              <div className='bg-muted border-l-4 border-border p-4 rounded-r-lg'>
                <p className='text-sm leading-6 text-muted-foreground'>
                  <strong className='text-foreground'>Pro Tip:</strong> Combine quick search with
                  filters for even more precise results. For example, search for a VIN and filter by
                  dealer to verify the correct location.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Exporting Data Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Exporting Data</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            Export recall data to CSV format for reporting, analysis, or sharing with external
            systems:
          </p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6 text-muted-foreground'>
              Exports respect your current filters and sorting
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Includes all visible columns
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Perfect for creating custom reports or manufacturer submissions
            </li>
          </ul>
        </section>

        {/* Mobile Experience Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Mobile Experience</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>On phones and tablets:</p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6 text-muted-foreground'>
              Table becomes horizontally scrollable
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Critical columns (VIN, Status, Actions) remain visible
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Touch-optimized checkboxes and buttons
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Consider using landscape orientation for more columns
            </li>
            <li className='text-sm leading-6 text-muted-foreground'>
              Filter panel collapses to save screen space
            </li>
          </ul>
        </section>

        {/* Best Practices Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Best Practices</h2>

          <div className='space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Combine Filters and Search</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Use filters to narrow by criteria (location, campaign, route assignment), then use
                  search to find specific vehicles within those filtered results.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Sort Before Selecting</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Sort the table by Dealer or Campaign before making bulk selections to ensure
                  you're selecting logically grouped recalls for routing.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Use Keyboard Shortcuts</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Learn the keyboard shortcuts to navigate and select recalls much faster than using
                  only the mouse. Power users report 3x improvement in speed!
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold'>Adjust Items Per Page</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  For bulk operations, increase items per page to 100 to see more recalls at once
                  and make larger selections without pagination.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm leading-6 text-muted-foreground mb-4'>
            Now that you understand the Recall List, explore these related features:
          </p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/filters' className='text-primary hover:underline'>
                Filters System
              </Link>{' '}
              - <span className='text-muted-foreground'>Master advanced filtering techniques</span>
            </li>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/recall-map' className='text-primary hover:underline'>
                Recall Map
              </Link>{' '}
              - <span className='text-muted-foreground'>View recalls geographically</span>
            </li>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/manual-routing' className='text-primary hover:underline'>
                Manual Routing
              </Link>{' '}
              -{' '}
              <span className='text-muted-foreground'>Assign selected recalls to technicians</span>
            </li>
          </ul>
        </section>
      </div>
    </DocsLayout>
  );
};
